// The browser is an ordinary client of the public API. Same bearer token, same endpoints,
// no privileged route -- if this file ever needs something Android cannot call, that is a
// design bug, not a feature.

import type {
  ApiErrorBody,
  Budget,
  CategorySuggestion,
  Device,
  Health,
  Lane,
  LaneDraft,
  LanePreview,
  Match,
  Page,
  PairingCode,
  PairingStatus,
  Reject,
  RescoreResult,
  SecurityReport,
  TestRun,
  Watch,
} from "./types";

const TOKEN_KEY = "trawler.token";

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string | null): void {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

export class ApiError extends Error {
  /** Branch on `code`, never on the message: messages are for humans and get reworded. */
  constructor(
    readonly status: number,
    message: string,
    readonly code: string = "unknown",
  ) {
    super(message);
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getToken();
  const headers = new Headers(init.headers);
  headers.set("Accept", "application/json");
  if (init.body) headers.set("Content-Type", "application/json");
  if (token) headers.set("Authorization", `Bearer ${token}`);

  const resp = await fetch(path, { ...init, headers });
  if (resp.status === 204) return undefined as T;
  const text = await resp.text();
  const body = text ? JSON.parse(text) : null;
  if (!resp.ok) {
    const err = body as Partial<ApiErrorBody> | null;
    const detail =
      typeof err?.detail === "string"
        ? err.detail
        : Array.isArray(err?.detail)
          ? (err.detail as { msg?: string }[]).map((d) => d.msg).join(", ")
          : resp.statusText;
    throw new ApiError(resp.status, detail, err?.code ?? "unknown");
  }
  return body as T;
}

function query(params: Record<string, string | number | boolean | null | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== null && value !== undefined && value !== "") search.set(key, String(value));
  }
  const qs = search.toString();
  return qs ? `?${qs}` : "";
}

export const api = {
  enrol: (bootstrap_token: string, name: string) =>
    request<{ id: string; name: string; platform: string; token: string }>(
      "/api/v1/devices/enrol",
      {
        method: "POST",
        body: JSON.stringify({ bootstrap_token, name, platform: "web" }),
      },
    ),
  me: () => request<Device>("/api/v1/devices/me"),

  // Pairing: an authenticated device mints a short-lived code a phone can scan. Deliberately
  // not the bootstrap token -- that one is permanent, and a QR of it is a QR of your front door.
  createPairingCode: (label?: string) =>
    request<PairingCode>(`/api/v1/devices/pair${label ? query({ label }) : ""}`, {
      method: "POST",
    }),
  pairingStatus: (id: string) => request<PairingStatus>(`/api/v1/devices/pair/${id}`),
  cancelPairingCode: (id: string) =>
    request<void>(`/api/v1/devices/pair/${id}`, { method: "DELETE" }),
  devices: () => request<Device[]>("/api/v1/devices"),
  revokeDevice: (id: string) =>
    request<void>(`/api/v1/devices/${id}`, { method: "DELETE" }),

  watches: () => request<Watch[]>("/api/v1/watches"),
  createWatch: (body: Partial<Watch>) =>
    request<Watch>("/api/v1/watches", { method: "POST", body: JSON.stringify(body) }),
  patchWatch: (id: string, body: Partial<Watch>) =>
    request<Watch>(`/api/v1/watches/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  deleteWatch: (id: string) => request<void>(`/api/v1/watches/${id}`, { method: "DELETE" }),
  testWatch: (
    id: string,
    body: {
      criteria?: string;
      extract_schema?: Record<string, unknown> | null;
      min_confidence?: number;
      max_price?: number | null;
      days?: number;
      limit?: number;
      lane_id?: number | null;
      include_stage0_rejects?: boolean;
      only_flagged?: boolean;
    },
  ) =>
    request<TestRun>(`/api/v1/watches/${id}/test`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  rescoreWatch: (
    id: string,
    body: {
      scope: "rejects" | "all";
      days: number;
      lane_id?: number | null;
      rerun_stage0: boolean;
      replay_alerts?: boolean;
    },
  ) =>
    request<RescoreResult>(`/api/v1/watches/${id}/rescore`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  resetLane: (
    id: number,
    body: {
      rerun_stage0: boolean;
      clear_verdicts: boolean;
      rejects_only: boolean;
      clear_watermark: boolean;
      days: number;
      replay_alerts?: boolean;
    },
  ) =>
    request<RescoreResult>(`/api/v1/lanes/${id}/reset`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  matches: (params: {
    watch_id?: string;
    since?: string;
    unacked?: boolean;
    min_confidence?: number;
    include_below_threshold?: boolean;
    limit?: number;
    offset?: number;
    sort?: "found" | "listed" | "confidence" | "price";
    order?: "asc" | "desc";
  }) => request<Page<Match>>(`/api/v1/matches${query(params)}`),
  bulkAck: (body: {
    verdict_ids?: number[];
    watch_id?: string;
    older_than_hours?: number;
    acked: boolean;
  }) =>
    request<{ affected: number }>("/api/v1/matches/ack", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  ackMatch: (verdictId: number, acked = true) =>
    request<Match>(`/api/v1/matches/${verdictId}/ack`, {
      method: "POST",
      body: JSON.stringify({ acked }),
    }),

  rejects: (params: {
    q?: string;
    watch_id?: string;
    stage?: "all" | "model" | "rules";
    flagged_only?: boolean;
    limit?: number;
    offset?: number;
  }) => request<Page<Reject>>(`/api/v1/rejects${query(params)}`),
  flagReject: (verdictId: number, flagged: boolean, note?: string) =>
    request<Reject>(`/api/v1/rejects/${verdictId}/flag`, {
      method: "POST",
      body: JSON.stringify({ flagged, note: note ?? null }),
    }),

  lanes: () => request<Lane[]>("/api/v1/lanes"),
  createLane: (body: LaneDraft) =>
    request<Lane>("/api/v1/lanes", { method: "POST", body: JSON.stringify(body) }),
  patchLane: (id: number, body: Partial<Lane>) =>
    request<Lane>(`/api/v1/lanes/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  deleteLane: (id: number) => request<void>(`/api/v1/lanes/${id}`, { method: "DELETE" }),
  previewLane: (body: LaneDraft) =>
    request<LanePreview>("/api/v1/lanes/preview", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  categories: (q: string, marketplace_id: string) =>
    request<CategorySuggestion[]>(
      `/api/v1/lanes/categories${query({ q, marketplace_id })}`,
    ),

  budget: () => request<Budget>("/api/v1/system/budget"),
  health: () => request<Health>("/api/v1/system/health"),

  security: () => request<SecurityReport>("/api/v1/system/security"),

  // EventSource cannot set headers, so /stream authenticates through the query string. A
  // single-use 60-second ticket goes there instead of the device token: query strings reach
  // proxy logs and browser history, and the device token does not expire.
  streamTicket: () =>
    request<{ ticket: string; expires_in: number }>("/api/v1/stream/ticket", { method: "POST" }),
  streamUrlWithTicket: (ticket: string) =>
    `/api/v1/stream?ticket=${encodeURIComponent(ticket)}`,
  /** Fallback for when ticket issue fails; the server still accepts the long-lived token. */
  streamUrl: () => `/api/v1/stream?token=${encodeURIComponent(getToken() ?? "")}`,
};
